import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, Clock, User, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { SEO } from '@/components/SEO';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { SocialShare } from '@/components/SocialShare';

const OverstimulationArticle = () => {
  const navigate = useNavigate();

  const handleGoBack = () => {
    navigate('/blog');
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "The Age of Overstimulation – How Our Nervous System Forgot to Rest",
    "description": "Explore how chronic overstimulation affects our nervous system and discover science-backed practices to restore natural rhythm and find rest in modern life.",
    "image": "https://mysticforest.club/lovable-uploads/overstimulation-mountains.jpg",
    "author": {
      "@type": "Person",
      "name": "Mystic Forest Team"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Mystic Forest",
      "logo": {
        "@type": "ImageObject",
        "url": "https://mysticforest.club/og-image.jpg"
      }
    },
    "datePublished": "2025-01-15",
    "dateModified": "2025-01-15"
  };

  return (
    <>
      <SEO
        title="The Age of Overstimulation – How Our Nervous System Forgot to Rest"
        description="Explore how chronic overstimulation affects our nervous system and discover science-backed practices to restore natural rhythm and find rest in modern life."
        canonicalUrl="https://mysticforest.club/articles/overstimulation"
        ogType="article"
        keywords={['overstimulation', 'nervous system', 'rest', 'parasympathetic', 'stress', 'digital detox', 'mindfulness']}
        article={{
          publishedTime: '2025-01-15',
          modifiedTime: '2025-01-15',
          author: 'Piotr',
          section: 'Wellness',
          tags: ['Wellness', 'Mindfulness', 'Rest']
        }}
        breadcrumbs={[
          { name: 'Blog', url: '/blog' },
          { name: 'The Age of Overstimulation', url: '/articles/overstimulation' }
        ]}
        jsonLd={jsonLd}
      />
      
      <div className="min-h-screen bg-background">
        <Navigation />
        
        <div className="max-w-6xl mx-auto px-6">
          <Breadcrumbs items={[
            { name: 'Blog', url: '/blog' },
            { name: 'The Age of Overstimulation', url: '/articles/overstimulation' }
          ]} />
        </div>
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-4xl mx-auto">
          <Button 
            variant="ghost" 
            onClick={handleGoBack}
            className="mb-8 -ml-4 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Blog
          </Button>
          
          <div className="mb-8">
            <Badge className="mb-4">Wellness</Badge>
            <h1 className="text-4xl md:text-6xl font-serif font-light mb-6 text-foreground leading-tight">
              The Age of Overstimulation – How Our Nervous System Forgot to Rest
            </h1>
            
            <div className="flex items-center gap-6 text-sm text-muted-foreground mb-8">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>January 15, 2025</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>8 min read</span>
              </div>
              <div className="flex items-center gap-2">
                <User className="w-4 h-4" />
                <span>Mystic Forest Team</span>
              </div>
            </div>
            
            <SocialShare 
              url="https://mysticforest.club/articles/overstimulation"
              title="The Age of Overstimulation – How Our Nervous System Forgot to Rest"
              description="Explore how chronic overstimulation affects our nervous system and discover science-backed practices to restore natural rhythm and find rest in modern life."
            />
          </div>
          
          <div className="relative overflow-hidden rounded-2xl mb-12">
            <img 
              src="/lovable-uploads/overstimulation-forest.jpg" 
              alt="Peaceful forest path representing nervous system restoration and natural calm"
              className="w-full h-[400px] md:h-[500px] object-cover grayscale"
              loading="eager"
            />
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="px-6 pb-24" itemScope itemType="https://schema.org/Article">
        <meta itemProp="headline" content="The Age of Overstimulation – How Our Nervous System Forgot to Rest" />
        <meta itemProp="datePublished" content="2025-01-15" />
        <meta itemProp="author" content="Mystic Forest Team" />
        
        <div className="max-w-4xl mx-auto">
          <div className="prose prose-lg max-w-none" itemProp="articleBody">
            <div className="text-xl leading-relaxed text-muted-foreground mb-8 font-light italic border-l-4 border-accent pl-8">
              "We are not meant to live with the volume up all the time."<br/>
              — Unknown
            </div>
            
            <div className="space-y-8 text-lg leading-relaxed text-foreground">
              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">The World Is Too Loud — Even When It's Quiet</h2>
              
              <p>
                If you've ever felt tired for no clear reason, struggled to focus, or found silence strangely uncomfortable, you're not alone.
                We live in an age of overstimulation — where our minds and bodies are constantly asked to respond, react, and adapt. Notifications, deadlines, traffic, small talk, podcasts, and endless scrolling — all of it feeds a nervous system that was never built for such noise.
              </p>

              <p>
                For most of human history, the nervous system followed a natural rhythm: moments of stress or alertness followed by recovery and calm. Today, we live mostly in the <em>on</em> position — constantly triggered, constantly plugged in.
              </p>

              <p>
                It's not weakness; it's biology. Our bodies haven't evolved as fast as our devices.
              </p>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">The Body Keeps the Score — Literally</h2>
              
              <p>
                Neuroscientists call this the "sympathetic dominance" — when the fight-or-flight branch of the nervous system stays chronically active.
                It's meant to help us survive, but not to live in.
              </p>

              <p>
                Dr. Bessel van der Kolk, author of <em>The Body Keeps the Score</em>, describes how prolonged stress rewires our systems: the body learns to stay alert, even when no threat exists. The result? Fatigue, tension, insomnia, and emotional burnout.
              </p>

              <p>
                Similarly, Dr. Gabor Maté argues in <em>When the Body Says No</em> that overstimulation and constant productivity often mask deeper disconnection — a culture where busyness has become a coping mechanism.
              </p>

              <p>
                Our minds may not always feel anxious, but our bodies remember the overload.
              </p>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">Overstimulation Feels Like This</h2>
              
              <ul className="space-y-3 ml-6">
                <li>You crave background noise even when you're alone.</li>
                <li>You check your phone mid-conversation, without meaning to.</li>
                <li>You feel restless in stillness.</li>
                <li>Sleep doesn't refresh you.</li>
                <li>You overthink when nothing's wrong.</li>
              </ul>

              <p>
                These are not signs of failure — they're symptoms of an environment that overwhelms the nervous system.
              </p>

              <p>
                Every notification triggers a micro-spike in stress hormones. Every scroll adds a small layer of stimulation. And without regular "parasympathetic breaks" — moments of calm where the body rebalances — the nervous system never gets to reset.
              </p>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">The Science of Restoration</h2>
              
              <p>
                Here's the hopeful part: recovery is built into us.
                The parasympathetic nervous system — often called "rest and digest" — has one job: to bring us back to equilibrium.
              </p>

              <p>
                Dr. Stephen Porges, creator of the Polyvagal Theory, explains that we can consciously engage this system through sensory regulation: breathing, grounding, and connection.
                Nature, silence, and slowness are not indulgences — they are biological tools for resetting the body's rhythm.
              </p>

              <p>Research supports this:</p>
              
              <ul className="space-y-3 ml-6">
                <li>A 1991 study by Roger Ulrich showed that hospital patients with a view of trees recovered faster.</li>
                <li>The Kaplans' Attention Restoration Theory (1995) found that time in nature replenishes cognitive capacity.</li>
                <li>Even two hours of weekly exposure to green space significantly lowers cortisol levels (White et al., 2019).</li>
              </ul>

              <p>
                In simple terms: our nervous system calms down when the world around us does.
              </p>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">How to Relearn Rest</h2>
              
              <p>
                Overcoming overstimulation isn't about escaping modern life — it's about reclaiming rhythm.
                Here are practices that help regulate your nervous system, wherever you are:
              </p>

              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-serif font-medium mb-3 text-foreground">1. Practice Micro-Stillness</h3>
                  <p>
                    Take 3–5 quiet minutes between tasks. Close your eyes. Unclench your jaw. Drop your shoulders. Let silence do its work.
                    Stillness, even in small doses, tells your body: <em>You are safe</em>.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-serif font-medium mb-3 text-foreground">2. Breathe to Reset</h3>
                  <p>
                    Slow, deep breathing activates the vagus nerve — the body's "calm switch."
                    Try box breathing: inhale for 4, hold for 4, exhale for 4, hold for 4. Repeat five times.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-serif font-medium mb-3 text-foreground">3. Create White Space</h3>
                  <p>
                    Every day, schedule one pocket of emptiness. No screens. No goals. No input. Just a walk, a gaze through the window, or a cup of tea without multitasking.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-serif font-medium mb-3 text-foreground">4. Seek Natural Silence</h3>
                  <p>
                    Silence isn't the absence of sound — it's the presence of space.
                    Go outside, even briefly. Notice the rhythm of wind, birds, or your footsteps. Natural sound restores nervous balance faster than artificial quiet.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-serif font-medium mb-3 text-foreground">5. Move Slowly</h3>
                  <p>
                    Gentle movement — stretching, yoga, or mindful walking — integrates energy back into the body. When we slow the body, the mind follows.
                  </p>
                </div>
              </div>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">What Happens When You Truly Rest</h2>
              
              <p>After a few weeks of conscious downshifting, most people notice:</p>
              
              <ul className="space-y-3 ml-6">
                <li><strong>Clearer thinking and focus.</strong></li>
                <li><strong>Less irritability and reactivity.</strong></li>
                <li><strong>Deeper, more restorative sleep.</strong></li>
                <li><strong>A subtle return of joy in small things</strong> — taste, touch, laughter.</li>
              </ul>

              <p>
                This isn't self-improvement. It's self-regulation — a return to your natural baseline.
              </p>

              <p>
                At Mystic Forest, we see this during every retreat. The first day feels like "detox." People reach for their phones, their thoughts race. By day two, they breathe differently. By day three, they laugh more easily. Their faces soften. The nervous system remembers peace.
              </p>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">The Modern Paradox</h2>
              
              <p>
                We often say, "I don't have time to rest."
                But in truth, it's rest that gives us time — because it gives us awareness.
              </p>

              <p>
                When we stop chasing stimulation, we rediscover attention, emotion, and creativity.
                Rest is not retreating from life; it's returning to it.
              </p>

              <div className="text-xl leading-relaxed text-accent mb-8 font-light italic border-l-4 border-accent pl-8 my-12">
                "Rest is not idleness, and to lie sometimes on the grass under trees on a summer's day, listening to the murmur of the water, or watching the clouds float across the sky, is by no means a waste of time."<br/>
                — John Lubbock
              </div>

              <div className="bg-card/50 rounded-xl p-8 my-12">
                <h3 className="text-2xl font-serif font-light mb-4 text-foreground">References & Recommended Reading</h3>
                <ul className="space-y-2 text-base">
                  <li>Bessel van der Kolk, <em>The Body Keeps the Score</em> (2014)</li>
                  <li>Gabor Maté, <em>When the Body Says No</em> (2003)</li>
                  <li>Stephen Porges, <em>Polyvagal Theory</em> (2011)</li>
                  <li>Roger Ulrich (1991), <em>View Through a Window May Influence Recovery from Surgery</em></li>
                  <li>Rachel & Stephen Kaplan (1995), <em>The Experience of Nature</em></li>
                  <li>White et al. (2019), Spending at least 120 minutes a week in nature is associated with good health and wellbeing.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </article>

        <Footer />
      </div>
    </>
  );
};

export default OverstimulationArticle;